/* msk-ascii-hero.js — MSK Phase 2, UNIT P2-A: "The Mask Materializing From Code"
 * REVISION 1 (2026-07-12) — art-direction pivot from Steven.
 *
 * v1 rested the mask at --msk-absolute #111 (near-invisible, ominous).
 * Steven's call: too subtle. The hero is now a SHOWPIECE — the snake-mask
 * emblem reads emphatically as a bone-white ASCII mask on black, assembles
 * out of its own streaming code on load, stays alive with running
 * characters, and pushes forward out of the screen (depth by brightness +
 * scale + cursor parallax + same-ink extrusion + a slow forward "advance"
 * breath). The cursor pushes THROUGH the code — characters scatter,
 * brighten, and recover.
 *
 * REVISION-1 authorized overrides (see UNIT-A-REVISION-1.md): the original
 * "never resolves / rests at #111 / never reaches skull / spiralCap 0.92"
 * constraints are LIFTED by Steven's direction. Skull is back in the ramp;
 * structural cells reach ash/skull; the spiral may hit skull. Still
 * STRICTLY MONOCHROME (void->absolute->charcoal->smoke->ash->skull, the
 * brand inks) — NO color, NO neon, NO chromatic split, NO gaussian/colored
 * glow. Depth is brightness/scale/parallax/extrusion, never emitted light.
 *
 * Grid: 96x88 luminance grid precomputed offline by gen_grid.py from
 * MSK-favicon-1024.png (alpha<10% = empty). 8 levels, 3 bits/cell,
 * 2 cells per base64 char (high 3 bits = first cell).
 *
 * Architecture: the resolved mask is painted onto two offscreen "depth"
 * layers (near = bright structure + spiral + face; far = dim interior).
 * A cheap composite loop draws those two layers each frame with per-layer
 * parallax + a global advance scale (smooth). A separate ~12fps cycle pass
 * mutates only the cells that change (living-code glyph churn, breathing
 * brightness, spiral drift + wrong-glyph trail, cursor push) and repaints
 * just those cells onto their layer — so per-frame cost stays bounded.
 * The build-in phase renders dynamically on the main canvas, then hands
 * off to the layered steady state.
 *
 * Perf: capped compositing, ~12fps mutation work, dirty-cell layer
 * repaint, IntersectionObserver + visibilitychange pause, DPR capped,
 * half density under 750px.
 * Reduced motion: ONE static, fully-bright, resolved frame — drawn
 * IMMEDIATELY and UNGATED in the constructor (so a backgrounded QA tab
 * still gets a verifiable painted frame). aria-hidden lives on the markup.
 *
 * No dependencies. Boots on canvas[data-msk-ascii]; re-scans on
 * shopify:section:load. Public surface: window.MSKAsciiHero.
 */
(function () {
  'use strict';

  if (window.MSKAsciiHero) return;

  /* ------------------------------------------------------------------ *
   * Grid data (generated — do not hand-edit; see units/A/gen_grid.py)
   * ------------------------------------------------------------------ */
  var GRID_W = 96;
  var GRID_H = 88;
  var B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  /* dark -> light display glyphs; engraving-plausible only (brief ramp) */
  var DISP = [' ', '·', ':', ';', '+', '*', '#', '%', '@'];
  var DISP_MAX = DISP.length - 1; /* 8 */

  var DATA =
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAAAAAAAAAAAJJIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAJJSKRSKSSRAAAAAAAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAAAAAKRJJJAAIABBSSRAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABSRBBJAIAAAAJJBKSIAAAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAABTRAAAAAAJTcjkjjRKbIAAAAAAAAAAAAAAAAAAAAAAAAAAAMtjbkjaTTkkt11uutkiJSIAAAAAAAAAAAAA' +
    'AAAAAAAAAAADlmjbltutttuuuuttut1kiBTAAAAAAAAAAAAAAAAAAAAAAACmtZBJcssltslstdsklc0lkZCaAAAAAAAAAAAA' +
    'AAAAAAAAAADl1saUkskbbTbjbjTabUUkskILhAAAAAAAAAAAAAAAAAAAAAbTsu21ibSSSSKKRJKJRSbktjZBcQAAAAAAAAAA' +
    'AAAAAAAAALZSSSTZRJJRKKRSSSaaSJTctsTAKbAAAAAAAAAAAAAAAAAACaIBKRSSSaSTaSSJJJBJSbSTskSQBLiAAAAAAAAA' +
    'AAAAAAAAbJIAAAAAAAAAAAAAAAAAALaU9siZBJcQAAAAAAAAAAAAAAAChJAIAAAAAAAAAAAAAAAAAATTksiSBJKiAAAAAAAA' +
    'AAAAAAAMJJIAAAAAAAAAAKctskaIAADaltaLABBcQAAAAAAAAAAAAAAjJIIAAAAAAAADttkbbcurRABbu0bLABAKhAAAAAAA' +
    'AAAAAAEiJIAAAAAAAAM1rRKKSBBTaRAcu0SLIBBJTIAAAAAAAAAAAATRJAAAAAAAAM1RKabTaSIBaJCduqSDIBIJLgAAAAAA' +
    'AAAAAAaJJBAAAAAADrRTkjbTaRIABAUm0bZDIBBJKbAAAAAAAAAAADRJIAAAAAAAjJTbkaSJJIBJSct0kbRLAJJJJUQAAAAA' +
    'AAAAAUJJAAAIAAANYSbbjJSccstmltt1iTISAIAJJKgAAAAAAAAAAaIAAAAAAABqCTSTUdt9u10kkjkbTKJgAAIBIJaAAAAA' +
    'AAAACZAAAAAAAAEYJTbstvut1urjjkciRJLQAAJABJUAAAAAAAAADIAAAAAAAAbKTlvu19tms1dkkjaSJJSAAAJAJIMQAAAA' +
    'AAAALAAAAAAAADakl11mtkjkUSjbUaRSJKJIAAJBAJKgAAAAAAAASAAAAAAAAUkt2tzjccTScTSaSKJJSRBQAAAAABBbAAAA' +
    'AAAARAAAAAAALltutkbbaaKCJRJKJJJJJAJZAIAAAAITAAAAAAACIAAAAAADl31tjSSSKJRJJJJJJJJAAIJSAAAAABALIAAA' +
    'AAACAAAAAAAdttsZSZJJJJJJIBIAAIIJRJSSAAAAAJICQAAAAAAKAAAAAAEmt1iSRJJJAAJJKSSSSSSaSRSLIAAAAABBYAAA' +
    'AAAKAAAAAAcutrSRJRJJJSkjcksksskjSSbTIAAAAAABZAAAAAARAAAAACk1jSRKIJKKbm2ttu1urJJTbSbRQAAAAAABaAAA' +
    'AAARAAAAADdsjRKRBJTctut2u22yAKIBaSTJTIAAAAAASAAAAAAZIAAAATlrSRSAKclu21t2u22ImttIKabJBhAAAAAATAAA' +
    'AABZAAAAAjmjJKIKdtu2/212u27FpAEyDibIAUAAAAAALAAAAABZAAAACcbiJZBLv23/+212u2okN2IkSaSIBLYAAAAADAAA' +
    'AABZAAAACajRKQJd3u/122t1u2SprMxMRTSAJRhAAAAADIAAAABZAAAACaSSSBKu2u7Ae22tuuMUUgdDiKRACZaAAAAACIAA' +
    'AABpAAAACaSSZCd22uoAN2tst1MTjkESbjSICiTAAAAAKIAAAAAiIAAABaZTQUl223YAO2ttt1MTccKZdbRITqLAAAAADIAA' +
    'AAAiIAAAAiSSIc22u3YAO20ttsETbcUKZTJCdjLAAAAALIAAAAAiAAAAAaKKJd/222gAm11stsDbkhTLZRAUljLAAAAAMIAA' +
    'AAAiAAAAALJKSl33229l221tksQqSCYCSITllbKAAAAAMIAAAAAbAAAAACQISl3222/3212tkjQMbkITJTsmjSTAAAAADAAA' +
    'AAAUIAAAAASIae221233+11sjaSAKIBaVmtkbRSAAAAALAAAAAAUIAAAAACQcVt1d2322t1sbSRIAMk1vtdjKJZAAAAALAAA' +
    'AAAUIAAAAAABdUsYet2utktjRSKcsuulsrkRRKYAAAAASAAAAAAEQAAAAAAANTlhMkkkjcbScruut1ssdaRJJTAAAAAAZAAA' +
    'AAAEYAAAAAAAFaczBaSSTbktuvs1tujjiKAJKhAAAAABYAAAAAADYAAAAAAADhSlIKLcsu2191djjiTSSRJJbIAAAAABYAAA' +
    'AAAChAAAAAAAAhJTactl201mlrjUSaKSJJJUYAAAAAACIAAAAAAAiAAAAAAAASBTkkt1tvrrcTbaaSRIIBTiAAAAAAACAAAA' +
    'AAAAjAAAAAAAACUtu23lsjcajbTZJIJBKTRAAAAAAAAKAAAAAAAALIAAAAAAAUt11rskLibbSRBBJJKSSSAAAAAAAAARAAAA' +
    'AAAAEQAAAAAADu2ltcaSSSIJABJSSSJIACaAAAAAAABQAAAAAAAAChAAAAAAVttkaSKJIBJJSSSJIAAAIALQAAAAAACQAAAA' +
    'AAAAAaAAAAABk0jaaJIBSSaSJAAAAABBIBJbAAAAAALAAAAAAAAAAMIAAAADtsjaJBKbSRAJBJJAABABAJJLYAAAAASAAAAA' +
    'AAAAADYAAAAEsjSJIKSIIBSbTSJBJBAJBJJJbAAAABYAAAAAAAAAAAiAAAALlbaJKRABTRBJIAKRJIBQJJJJMIAAACQAAAAA' +
    'AAAAAAUAAAATbiJBZABKc2kbbcbRJAKAIJKSKYAAAKAAAAAAAAAAAADYAAAUTSJLIBJScu3/+0SJJBQBKJTaRaAAAQAAAAAA' +
    'AAAAAAAiIAAEKSJKBBSScl2u1rSJAKAJKZT0RSAACQAAAAAAAAAAAAAMJAADYSRJCISKTctlkaRIBZBQALUsZTAASAAAAAAA' +
    'AAAAAAAChIAAaBJJJSKaKTjkaRJASIKAADUrKTACYAAAAAAAAAAAAAAAbIAADZJJJKZLSKbbRJALQBQAADdjRLALAAAAAAAA' +
    'AAAAAAAADZAAAURJKRJATRRJBBKRAKAAADdzRTBhAAAAAAAAAAAAAAAAAbIAAATSIJJAASRJJKJACQAAATujRZMQAAAAAAAA' +
    'AAAAAAAAACZAAAATSSSJIAJRJAAKRAAABctZKZiAAAAAAAAAAAAAAAAAAALQAAAABJJKSJAAJSSRAAAAMmjJLTIAAAAAAAAA' +
    'AAAAAAAAAAAaJAAAAAAAAJSSRJAAAAALlsRJbYAAAAAAAAAAAAAAAAAAAAADZAAAAAABTbbkbRJJJKUmtaJLiAAAAAAAAAAA' +
    'AAAAAAAAAAAATRAAAABckjTdlt0skmt1aIBbIAAAAAAAAAAAAAAAAAAAAAAAASIAAAUiIJJSSklllskSBJTQAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAASRACiAAAAAJJSRSRJJKcYAAAAAAAAAAAAAAAAAAAAAAAAAAAASILAAAAAAABJRJKJSTQAAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAAAAKZIAAAAAAAAAAAKSIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAKaRIAAAAAAJJTSIAAAAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAAAAAABSSSSSRSSSSIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAJBAJAAAAAAAAAAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';

  /* Right-eye spiral geometry in full-grid cell space (measured by
   * gen_grid.py from the 1024px source; see units/A/spiral_check.png). */
  var SPIRAL = { cx: 58.6, cy: 42.5, rx: 5.8, ry: 7.4, trailEndX: 70.8 };

  /* ------------------------------------------------------------------ *
   * Tunables — all dial-able. Brightness / depth / advance / build-in
   * are the rev-1 headline knobs and are grouped first.
   * ------------------------------------------------------------------ */
  var CFG = {
    /* --- brightness (rev1 §1): map source levels bright, into ash/skull */
    brightFloor: 0.34,     /* dimmest non-void cell (0..1 along void->skull) */
    brightGain: 1.12,      /* >1 pushes structural highs into skull */
    brightGamma: 0.82,     /* <1 lifts mids so the mask reads full */
    spiralFloor: 0.86,     /* spiral resting brightness (may reach skull) */
    spiralDrift: 0.14,     /* slow extra spiral lift on top of floor */
    spiralPeriod: 11,      /* s — spiral slow drift */

    /* --- depth / Z-emergence (rev1 §2) */
    scaleBase: 0.74,       /* font size = cell*(scaleBase + scaleGain*frac) */
    scaleGain: 0.5,        /* bright cells render bigger => nearer */
    nearThresh: 0.62,      /* brightness frac at/above => "near" depth layer */
    parallaxPx: 24,        /* max near-layer cursor parallax offset (css px) */
    parallaxLerp: 0.09,    /* easing toward the parallax target */
    nearK: 1.0,            /* near layer parallax multiplier */
    farK: 0.38,            /* far layer parallax multiplier (moves less) */
    advanceAmp: 0.035,     /* forward "advance" breath: scale 1.00 -> 1.035 */
    advancePeriod: 13,     /* s — advance breath cycle */
    extrudePx: 2,          /* same-ink extrusion offset (light from up-left) */
    extrudeMin: 0.58,      /* only cells this bright cast extrusion shadow */
    extrudeDrop: 12,       /* palette steps darker for the extrusion shadow */

    /* --- materialize-from-code (rev1 §3) */
    buildMs: 2200,         /* build-in duration */
    buildCellWin: 0.22,    /* per-cell arrival window (frac of buildMs) */
    cycleBase: 0.02,       /* base per-cell living-code re-glyph chance/tick */
    cycleEdge: 0.11,       /* extra re-glyph chance scaled by edge/spiral wt */

    /* --- cursor push-through (rev1 §4), amplified */
    cursorRadiusPx: 104,   /* px — push radius */
    cursorBrighten: 10,    /* palette steps brighter at full push */
    cursorDecay: 0.9,      /* per-tick corruption falloff */
    cursorPushPx: 12,      /* extra near-layer shove toward pointer */

    /* --- breathing (regional, from v1) */
    latticeX: 13,
    latticeY: 12,
    breathPeriodMin: 8,
    breathPeriodMax: 14,
    breathAmp: 6,          /* palette steps of regional breathing lift */

    /* --- strike (from v1) */
    strikeMinS: 10,
    strikeMaxS: 25,

    /* --- trail wrong-glyph noise off the spiral edge (from v1) */
    trailChance: 0.18,

    /* --- scheduling / perf */
    cycleFps: 12,          /* mutation work cap */
    compositeFps: 30,      /* parallax/advance composite cap */
    mobileMaxPx: 749,
    paletteSize: 48,
    dprCap: 2,
    layerDprCap: 2         /* offscreen layers may use a lower dpr to save RAM */
  };

  var MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */
  function decodeLevels() {
    var n = GRID_W * GRID_H;
    var out = new Uint8Array(n);
    for (var i = 0; i < n; i += 2) {
      var v = B64.indexOf(DATA.charAt(i >> 1));
      if (v < 0) v = 0;
      out[i] = v >> 3;
      out[i + 1] = v & 7;
    }
    return out;
  }

  function cssColor(name, fallback) {
    try {
      var v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      return v || fallback;
    } catch (e) {
      return fallback;
    }
  }

  function hexToRgb(hex) {
    var h = hex.replace('#', '').trim();
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    if (isNaN(n)) return [17, 17, 17];
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function mix(a, b, t) {
    return [
      Math.round(a[0] + (b[0] - a[0]) * t),
      Math.round(a[1] + (b[1] - a[1]) * t),
      Math.round(a[2] + (b[2] - a[2]) * t)
    ];
  }

  function rgbStr(c) { return 'rgb(' + c[0] + ',' + c[1] + ',' + c[2] + ')'; }

  function clampI(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  /* Palette spans the full brand ramp void -> absolute -> charcoal ->
   * smoke -> ash -> skull. Rev-1 restores skull: index 0 = void, the top
   * = skull. Strictly the brand inks, nothing between-hue. */
  function buildPalette() {
    var stops = [
      [0.00, hexToRgb(cssColor('--msk-void', '#080808'))],
      [0.10, hexToRgb(cssColor('--msk-absolute', '#111111'))],
      [0.26, hexToRgb(cssColor('--msk-charcoal', '#1C1C1C'))],
      [0.44, hexToRgb(cssColor('--msk-smoke', '#2E2E2E'))],
      [0.72, hexToRgb(cssColor('--msk-ash', '#9A9490'))],
      [1.00, hexToRgb(cssColor('--msk-skull', '#E8E3DA'))]
    ];
    var n = CFG.paletteSize;
    var pal = new Array(n);
    for (var i = 0; i < n; i++) {
      var t = i / (n - 1);
      var s = 1;
      while (s < stops.length - 1 && stops[s][0] < t) s++;
      var t0 = stops[s - 1][0], t1 = stops[s][0];
      var f = t1 > t0 ? (t - t0) / (t1 - t0) : 0;
      pal[i] = rgbStr(mix(stops[s - 1][1], stops[s][1], f));
    }
    return { colors: pal, voidStr: rgbStr(stops[0][1]) };
  }

  /* level (0..7) -> brightness fraction (0..1 along the palette) */
  function levelFrac(level) {
    if (level <= 0) return 0;
    var g = Math.pow(level / 7, CFG.brightGamma);
    var f = CFG.brightFloor + g * (1 - CFG.brightFloor);
    f *= CFG.brightGain;
    return f > 1 ? 1 : f;
  }

  var LEVELS = decodeLevels();

  /* A grid "view" at a density step (1 = full, 2 = halved for <750px). */
  function makeView(step, palN) {
    var w = Math.ceil(GRID_W / step);
    var h = Math.ceil(GRID_H / step);
    var n = w * h;
    var level = new Uint8Array(n);
    var baseCi = new Uint8Array(n);      /* resting palette index */
    var frac = new Float32Array(n);      /* brightness fraction 0..1 */
    var layer = new Uint8Array(n);       /* 0 far, 1 near */
    var edge = new Float32Array(n);      /* living-code cycle weight */
    var phase = new Float32Array(n);
    var spiral = new Float32Array(n);
    var buildDelay = new Float32Array(n);
    var trailIdx = [];
    var trailStr = [];
    var maxI = palN - 1;
    var diag = Math.sqrt(GRID_W * GRID_W + GRID_H * GRID_H);
    var x, y, i, gx, gy, by, bx;

    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        i = y * w + x;
        /* max-pool the block so thin engraving lines survive halving */
        var lv = 0;
        for (by = 0; by < step; by++) {
          for (bx = 0; bx < step; bx++) {
            var sx = x * step + bx, sy = y * step + by;
            if (sx < GRID_W && sy < GRID_H) {
              var l = LEVELS[sy * GRID_W + sx];
              if (l > lv) lv = l;
            }
          }
        }
        level[i] = lv;
        var fr = levelFrac(lv);
        frac[i] = fr;
        baseCi[i] = Math.round(fr * maxI);
        layer[i] = fr >= CFG.nearThresh ? 1 : 0;
        edge[i] = fr;                         /* bright == structural edge */
        phase[i] = ((i * 2654435761 >>> 0) % 6283) / 1000;

        gx = (x + 0.5) * step - 0.5;
        gy = (y + 0.5) * step - 0.5;
        var dx = (gx - SPIRAL.cx) / SPIRAL.rx;
        var dy = (gy - SPIRAL.cy) / SPIRAL.ry;
        var d = Math.sqrt(dx * dx + dy * dy);
        var sp = d < 1.15 ? Math.max(0, 1 - d / 1.15) : 0;
        spiral[i] = sp;
        if (sp > 0) { layer[i] = 1; edge[i] = 1; }

        /* build-in: cells crystallize radially outward from the spiral eye
         * (the emblem's "source"), plus a little jitter, over ~80% of the
         * build; each cell then takes buildCellWin to settle. */
        var ddx = gx - SPIRAL.cx, ddy = gy - SPIRAL.cy;
        var dr = Math.sqrt(ddx * ddx + ddy * ddy) / diag;
        var jit = ((i * 40503 + 12289) % 1000) / 1000 * 0.12;
        buildDelay[i] = Math.min(0.78, dr * 1.5 + jit);

        var tx0 = SPIRAL.cx + SPIRAL.rx * 0.5;
        if (gx >= tx0 && gx <= SPIRAL.trailEndX &&
            Math.abs(gy - SPIRAL.cy) <= SPIRAL.ry * 0.8) {
          var str = 1 - (gx - tx0) / (SPIRAL.trailEndX - tx0);
          if (level[i] === 0) str *= 0.5;
          if (str > 0.05) { trailIdx.push(i); trailStr.push(str); }
        }
      }
    }

    return {
      step: step, w: w, h: h, n: n,
      level: level, baseCi: baseCi, frac: frac, layer: layer, edge: edge,
      phase: phase, spiral: spiral, buildDelay: buildDelay,
      trailIdx: trailIdx, trailStr: trailStr,
      trailTtl: new Uint8Array(n),
      curCi: new Int16Array(n),            /* current palette index */
      curG: new Uint8Array(n),             /* current display glyph index */
      corrupt: new Float32Array(n),        /* cursor push amount */
      hot: []                              /* indices with corrupt > 0 */
    };
  }

  function glyphForLevel(lv) { return clampI(1 + Math.round(lv / 7 * (DISP_MAX - 1)), 1, DISP_MAX); }

  /* ------------------------------------------------------------------ *
   * Instance
   * ------------------------------------------------------------------ */
  function AsciiHero(canvas) {
    this.canvas = canvas;
    this.root = canvas.closest('.msk-hero') || canvas.parentElement || canvas;
    this.ctx = canvas.getContext('2d');
    if (!this.ctx) return;

    this.pal = buildPalette();
    this.views = {};
    this.view = null;
    this.layers = null;          /* [{canvas,ctx}] far, near */
    this.running = false;
    this.rafId = 0;
    this.inView = true;
    this.docVisible = !document.hidden;
    this.mode = 'idle';          /* idle | build | steady */
    this.buildStart = 0;
    this.lastComposite = 0;
    this.cycleAccum = 0;
    this.frame = 0;

    /* parallax + advance runtime */
    this.px = 0; this.py = 0;          /* current parallax offset */
    this.tpx = 0; this.tpy = 0;        /* target */
    this.pointer = { active: false, x: 0, y: 0 };

    /* breathing lattice */
    var LN = CFG.latticeX * CFG.latticeY;
    this.latPhase = new Float32Array(LN);
    this.latSpeed = new Float32Array(LN);
    this.latVal = new Float32Array(LN);
    for (var i = 0; i < LN; i++) {
      var h1 = (i * 40503 + 12289) % 10000 / 10000;
      var h2 = (i * 65521 + 4099) % 10000 / 10000;
      this.latPhase[i] = h1 * Math.PI * 2;
      var period = CFG.breathPeriodMin + h2 * (CFG.breathPeriodMax - CFG.breathPeriodMin);
      this.latSpeed[i] = (Math.PI * 2) / period;
    }

    this.t0 = performance.now();
    this.nextStrike = this.t0 + this.randRange(CFG.strikeMinS, CFG.strikeMaxS) * 1000;
    this.strike = null;

    this.mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    this.mqHover = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;

    this.bind();
    this.layout();

    if (this.reduced()) {
      /* rev1 QA guarantee: paint the resolved frame NOW, ungated. */
      this.drawStatic();
    } else {
      this.maybeRun();
    }
  }

  AsciiHero.prototype.randRange = function (a, b) { return a + Math.random() * (b - a); };
  AsciiHero.prototype.reduced = function () { return !!(this.mqReduce && this.mqReduce.matches); };

  AsciiHero.prototype.bind = function () {
    var self = this;

    this.onVis = function () { self.docVisible = !document.hidden; self.maybeRun(); };
    document.addEventListener('visibilitychange', this.onVis);

    this.io = null;
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) self.inView = entries[i].isIntersecting;
        self.maybeRun();
      }, { threshold: 0 });
      this.io.observe(this.canvas);
    }

    this.onResize = function () {
      clearTimeout(self.resizeTimer);
      self.resizeTimer = setTimeout(function () {
        var hadSteady = self.mode === 'steady';
        self.layout();
        if (self.reduced()) self.drawStatic();
        else if (hadSteady) self.enterSteady(); /* re-render layers at new size */
      }, 150);
    };
    window.addEventListener('resize', this.onResize);

    this.onMq = function () {
      if (self.reduced()) { self.stop(); self.mode = 'idle'; self.layout(); self.drawStatic(); }
      else { self.layout(); self.mode = 'idle'; self.maybeRun(); }
    };
    if (this.mqReduce) {
      if (this.mqReduce.addEventListener) this.mqReduce.addEventListener('change', this.onMq);
      else if (this.mqReduce.addListener) this.mqReduce.addListener(this.onMq);
    }

    this.onMove = function (ev) {
      if (!self.mqHover || !self.mqHover.matches) return;
      var r = self.canvas.getBoundingClientRect();
      self.pointer.x = ev.clientX - r.left;
      self.pointer.y = ev.clientY - r.top;
      self.pointer.active = true;
    };
    this.onLeave = function () { self.pointer.active = false; };
    this.root.addEventListener('pointermove', this.onMove);
    this.root.addEventListener('pointerleave', this.onLeave);
  };

  AsciiHero.prototype.destroy = function () {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVis);
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimer);
    if (this.io) this.io.disconnect();
    if (this.mqReduce) {
      if (this.mqReduce.removeEventListener) this.mqReduce.removeEventListener('change', this.onMq);
      else if (this.mqReduce.removeListener) this.mqReduce.removeListener(this.onMq);
    }
    this.root.removeEventListener('pointermove', this.onMove);
    this.root.removeEventListener('pointerleave', this.onLeave);
    this.layers = null;
    if (this.canvas) this.canvas.__mskAscii = null;
  };

  /* ------------------------------------------------------------------ *
   * Layout
   * ------------------------------------------------------------------ */
  AsciiHero.prototype.layout = function () {
    var cssW = this.root.clientWidth || window.innerWidth;
    var cssH = this.root.clientHeight || window.innerHeight;
    if (!cssW || !cssH) return;

    var step = window.innerWidth <= CFG.mobileMaxPx ? 2 : 1;
    if (!this.views[step]) this.views[step] = makeView(step, CFG.paletteSize);
    this.view = this.views[step];

    this.dpr = Math.min(window.devicePixelRatio || 1, CFG.dprCap);
    this.ldpr = Math.min(this.dpr, CFG.layerDprCap);
    this.cssW = cssW; this.cssH = cssH;

    this.canvas.width = Math.round(cssW * this.dpr);
    this.canvas.height = Math.round(cssH * this.dpr);
    this.canvas.style.width = cssW + 'px';
    this.canvas.style.height = cssH + 'px';
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    var v = this.view;
    this.cell = Math.max(cssW / v.w, cssH / v.h);
    this.offX = (cssW - v.w * this.cell) / 2;
    this.offY = (cssH - v.h * this.cell) / 2;

    /* font-size buckets keyed to the per-cell brightness scale */
    this.fontBuckets = [];
    for (var b = 0; b <= 12; b++) {
      var sc = CFG.scaleBase + (b / 12) * CFG.scaleGain;
      this.fontBuckets.push((this.cell * sc).toFixed(2) + 'px ' + MONO);
    }
    this.ctx.font = this.fontBuckets[6];
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';

    /* (re)build offscreen depth layers */
    this.layers = null;
  };

  AsciiHero.prototype.fontFor = function (frac) {
    var b = clampI(Math.round(frac * 12), 0, 12);
    return this.fontBuckets[b];
  };

  AsciiHero.prototype.ensureLayers = function () {
    if (this.layers) return;
    var mk = function (self) {
      var c = document.createElement('canvas');
      c.width = Math.round(self.cssW * self.ldpr);
      c.height = Math.round(self.cssH * self.ldpr);
      var x = c.getContext('2d');
      x.setTransform(self.ldpr, 0, 0, self.ldpr, 0, 0);
      x.textAlign = 'center';
      x.textBaseline = 'middle';
      return { canvas: c, ctx: x };
    };
    this.layers = [mk(this), mk(this)]; /* [far, near] */
  };

  AsciiHero.prototype.maybeRun = function () {
    if (this.reduced()) return;
    if (this.inView && this.docVisible) this.start();
    else this.stop();
  };

  AsciiHero.prototype.start = function () {
    if (this.running || !this.ctx) return;
    this.running = true;
    if (this.mode === 'idle') { this.mode = 'build'; this.buildStart = performance.now(); }
    var self = this;
    this.rafId = requestAnimationFrame(function loop(ts) {
      if (!self.running) return;
      self.rafId = requestAnimationFrame(loop);
      self.onFrame(ts);
    });
  };

  AsciiHero.prototype.stop = function () {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  };

  /* ------------------------------------------------------------------ *
   * Frame dispatch
   * ------------------------------------------------------------------ */
  AsciiHero.prototype.onFrame = function (ts) {
    if (this.mode === 'build') {
      var e = ts - this.buildStart;
      var interval = 1000 / 15;
      if (e >= CFG.buildMs) { this.enterSteady(); this.onFrame(ts); return; }
      if (ts - this.lastComposite < interval - 4) return;
      this.lastComposite = ts;
      this.drawBuildFrame(e / CFG.buildMs);
      return;
    }
    if (this.mode === 'steady') {
      /* cycle (mutation) work — gated to cycleFps */
      var cInt = 1000 / CFG.cycleFps;
      this.cycleAccum += ts - (this.lastCycleTs || ts);
      this.lastCycleTs = ts;
      if (this.cycleAccum >= cInt) {
        this.cycleAccum = this.cycleAccum % cInt;
        this.frame++;
        this.cycle(ts);
      }
      /* composite — gated to compositeFps */
      var pInt = 1000 / CFG.compositeFps;
      if (ts - this.lastComposite >= pInt - 2) {
        this.lastComposite = ts;
        this.composite(ts);
      }
    }
  };

  AsciiHero.prototype.enterSteady = function () {
    this.ensureLayers();
    var v = this.view;
    /* seed current state from the resting resolve */
    for (var i = 0; i < v.n; i++) {
      v.curCi[i] = v.baseCi[i];
      v.curG[i] = v.level[i] ? glyphForLevel(v.level[i]) : 0;
    }
    /* clear + paint every cell to its layer */
    for (var L = 0; L < this.layers.length; L++) {
      this.layers[L].ctx.clearRect(0, 0, this.cssW, this.cssH);
    }
    for (i = 0; i < v.n; i++) if (v.level[i] || v.spiral[i] > 0) this.paintCell(i);
    this.mode = 'steady';
    this.lastCycleTs = performance.now();
    this.composite(performance.now());
  };

  /* Paint one cell onto its depth layer (clear its region first). */
  AsciiHero.prototype.paintCell = function (i) {
    var v = this.view;
    var g = v.curG[i];
    var L = this.layers[v.layer[i]];
    var lctx = L.ctx;
    var x = i % v.w, y = (i / v.w) | 0;
    var colX = this.offX + x * this.cell;
    var rowY = this.offY + y * this.cell;
    var pad = this.cell * 0.75;
    lctx.clearRect(colX - pad, rowY - pad, this.cell + pad * 2, this.cell + pad * 2);
    if (g === 0) return;
    var cx = colX + this.cell / 2, cy = rowY + this.cell / 2;
    lctx.font = this.fontFor(v.frac[i]);
    var ci = v.curCi[i];
    var ch = DISP[g];
    if (v.frac[i] >= CFG.extrudeMin) {
      lctx.fillStyle = this.pal.colors[clampI(ci - CFG.extrudeDrop, 0, CFG.paletteSize - 1)];
      lctx.fillText(ch, cx + CFG.extrudePx, cy + CFG.extrudePx);
    }
    lctx.fillStyle = this.pal.colors[clampI(ci, 0, CFG.paletteSize - 1)];
    lctx.fillText(ch, cx, cy);
  };

  /* ------------------------------------------------------------------ *
   * Steady: mutation (cycle) pass — ~12fps, repaints only changed cells
   * ------------------------------------------------------------------ */
  AsciiHero.prototype.cycle = function (now) {
    var v = this.view;
    var t = (now - this.t0) / 1000;
    var maxI = CFG.paletteSize - 1;
    var LX = CFG.latticeX, LY = CFG.latticeY;
    var li;
    for (li = 0; li < LX * LY; li++) {
      this.latVal[li] = 0.5 + 0.5 * Math.sin(t * this.latSpeed[li] + this.latPhase[li]);
    }
    var spiralPulse = 0.5 + 0.5 * Math.sin(t * (Math.PI * 2) / CFG.spiralPeriod + 1.7);

    /* strike scheduling */
    if (this.strike) {
      if (--this.strike.framesLeft <= 0) { this.strike = null; this.nextStrike = now + this.randRange(CFG.strikeMinS, CFG.strikeMaxS) * 1000; }
    } else if (now >= this.nextStrike) {
      var bh = 2 + Math.floor(Math.random() * 3);
      var y0 = 4 + Math.floor(Math.random() * (v.h - bh - 8));
      this.strike = { y0: y0, y1: y0 + bh - 1, offCells: (Math.random() < 0.5 ? -1 : 1) * (1 + Math.floor(Math.random() * 2)), framesLeft: 1 + Math.floor(Math.random() * 2) };
    }

    /* trail: wrong-glyph pixel noise off the spiral's right edge */
    var k, ti;
    for (k = 0; k < v.trailIdx.length; k++) {
      ti = v.trailIdx[k];
      if (v.trailTtl[ti] > 0) { v.trailTtl[ti]--; if (v.trailTtl[ti] === 0) this.recolorCell(ti, t, spiralPulse, maxI); }
      else if (Math.random() < CFG.trailChance * v.trailStr[k]) {
        v.curG[ti] = 1 + Math.floor(Math.random() * DISP_MAX);
        v.trailTtl[ti] = 1 + Math.floor(Math.random() * 3);
        this.paintCell(ti);
      }
    }

    /* cursor push bookkeeping */
    if (this.pointer.active) this.injectCorruption();
    var hot = v.hot;
    for (k = hot.length - 1; k >= 0; k--) {
      var hi = hot[k];
      v.corrupt[hi] *= CFG.cursorDecay;
      if (v.corrupt[hi] < 0.05) { v.corrupt[hi] = 0; hot[k] = hot[hot.length - 1]; hot.pop(); this.recolorCell(hi, t, spiralPulse, maxI); }
    }

    /* regional breathing + spiral drift + living-code glyph churn */
    var fx = (LX - 1) / v.w, fy = (LY - 1) / v.h;
    var x, y, i;
    for (y = 0; y < v.h; y++) {
      var rowBase = y * v.w;
      for (x = 0; x < v.w; x++) {
        i = rowBase + x;
        if (!v.level[i] && v.spiral[i] === 0) continue;
        if (v.trailTtl[i] > 0) continue; /* trail owns this cell now */

        var changed = false;
        /* living-code: re-roll the glyph, weighted to edges/spiral */
        var p = CFG.cycleBase + CFG.cycleEdge * v.edge[i];
        if (v.corrupt[i] > 0.05) p += 0.4; /* cursor makes it churn hard */
        if (Math.random() < p) {
          var lv = v.level[i] || 1;
          var gi = clampI(glyphForLevel(lv) + (Math.floor(Math.random() * 3) - 1), 1, DISP_MAX);
          if (v.corrupt[i] > 0.05) gi = 1 + Math.floor(Math.random() * DISP_MAX);
          if (gi !== v.curG[i]) { v.curG[i] = gi; changed = true; }
        }

        var ci = this.colorFor(i, x, y, fx, fy, spiralPulse, maxI);
        if (ci !== v.curCi[i]) { v.curCi[i] = ci; changed = true; }
        if (changed) this.paintCell(i);
      }
    }
  };

  /* recompute a cell's resting color (breathing + spiral) and repaint */
  AsciiHero.prototype.recolorCell = function (i, t, spiralPulse, maxI) {
    var v = this.view;
    var x = i % v.w, y = (i / v.w) | 0;
    var fx = (CFG.latticeX - 1) / v.w, fy = (CFG.latticeY - 1) / v.h;
    if (v.curG[i] === 0 && v.level[i]) v.curG[i] = glyphForLevel(v.level[i]);
    v.curCi[i] = this.colorFor(i, x, y, fx, fy, spiralPulse, maxI);
    this.paintCell(i);
  };

  AsciiHero.prototype.colorFor = function (i, x, y, fx, fy, spiralPulse, maxI) {
    var v = this.view;
    /* bilinear breathing sample */
    var u = x * fx, w0 = y * fy;
    var iu = u | 0, iv = w0 | 0, fu = u - iu, fv = w0 - iv;
    var LX = CFG.latticeX;
    var l00 = this.latVal[iv * LX + iu];
    var l10 = this.latVal[iv * LX + iu + 1];
    var l01 = this.latVal[(iv + 1) * LX + iu];
    var l11 = this.latVal[(iv + 1) * LX + iu + 1];
    var nz = l00 + (l10 - l00) * fu + (l01 - l00 + (l11 - l10 - l01 + l00) * fu) * fv;

    var ci = v.baseCi[i] + Math.round((nz - 0.5) * CFG.breathAmp);

    if (v.spiral[i] > 0) {
      var sp = v.spiral[i] * (CFG.spiralFloor + CFG.spiralDrift * spiralPulse);
      var sci = Math.round(sp * maxI);
      if (sci > ci) ci = sci;
    }
    var c = v.corrupt[i];
    if (c > 0.05) ci += Math.round(c * CFG.cursorBrighten); /* push = brighten */
    return clampI(ci, 0, maxI);
  };

  AsciiHero.prototype.injectCorruption = function () {
    var v = this.view;
    var rc = CFG.cursorRadiusPx / this.cell;
    var cx = (this.pointer.x - this.offX) / this.cell;
    var cy = (this.pointer.y - this.offY) / this.cell;
    var x0 = Math.max(0, Math.floor(cx - rc)), x1 = Math.min(v.w - 1, Math.ceil(cx + rc));
    var y0 = Math.max(0, Math.floor(cy - rc)), y1 = Math.min(v.h - 1, Math.ceil(cy + rc));
    for (var y = y0; y <= y1; y++) {
      for (var x = x0; x <= x1; x++) {
        var i = y * v.w + x;
        if (!v.level[i]) continue; /* the pointer pushes the mask, not the void */
        var dx = x + 0.5 - cx, dy = y + 0.5 - cy;
        var d2 = dx * dx + dy * dy;
        if (d2 > rc * rc) continue;
        if (v.corrupt[i] === 0) v.hot.push(i);
        var add = 0.55 * (1 - Math.sqrt(d2) / rc);
        v.corrupt[i] = Math.min(1, v.corrupt[i] + add);
      }
    }
  };

  /* ------------------------------------------------------------------ *
   * Steady: composite pass — parallax + advance, cheap (few drawImages)
   * ------------------------------------------------------------------ */
  AsciiHero.prototype.composite = function (now) {
    if (!this.layers) return;
    var ctx = this.ctx, t = (now - this.t0) / 1000;
    var cw = this.cssW, ch = this.cssH;

    /* parallax target from pointer (near layer); eases back to 0 at rest */
    if (this.pointer.active && this.mqHover && this.mqHover.matches) {
      this.tpx = ((this.pointer.x / cw) - 0.5) * 2 * CFG.parallaxPx;
      this.tpy = ((this.pointer.y / ch) - 0.5) * 2 * CFG.parallaxPx;
    } else { this.tpx = 0; this.tpy = 0; }
    this.px += (this.tpx - this.px) * CFG.parallaxLerp;
    this.py += (this.tpy - this.py) * CFG.parallaxLerp;

    /* cursor "push toward the glass": extra near shove toward the pointer */
    var pushX = 0, pushY = 0;
    if (this.pointer.active && this.mqHover && this.mqHover.matches) {
      pushX = ((this.pointer.x / cw) - 0.5) * 2 * CFG.cursorPushPx;
      pushY = ((this.pointer.y / ch) - 0.5) * 2 * CFG.cursorPushPx;
    }

    /* advance breath (forward scale) */
    var s = 1 + CFG.advanceAmp * (0.5 + 0.5 * Math.sin(t * (Math.PI * 2) / CFG.advancePeriod));
    var cx = cw / 2, cy = ch / 2;

    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = this.pal.voidStr;
    ctx.fillRect(0, 0, cw, ch);

    var K = [CFG.farK, CFG.nearK];
    for (var L = 0; L < this.layers.length; L++) {
      var ox = this.px * K[L] + (L === 1 ? pushX : 0);
      var oy = this.py * K[L] + (L === 1 ? pushY : 0);
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.translate(cx + ox, cy + oy);
      ctx.scale(s, s);
      ctx.translate(-cx, -cy);
      ctx.drawImage(this.layers[L].canvas, 0, 0, cw, ch);
    }
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    /* strike: "struck twice" — overlay near-layer band, offset, 1-2 frames */
    if (this.strike) {
      var offPx = this.strike.offCells * this.cell;
      var bandTop = this.offY + this.strike.y0 * this.cell;
      var bandH = (this.strike.y1 - this.strike.y0 + 1) * this.cell;
      var near = this.layers[1].canvas;
      ctx.drawImage(
        near,
        0, bandTop * this.ldpr, near.width, bandH * this.ldpr,
        offPx + this.px * CFG.nearK, bandTop, cw, bandH
      );
    }
  };

  /* ------------------------------------------------------------------ *
   * Build-in: dynamic render on the main canvas — the mask assembles
   * from streaming code radiating out of the spiral eye.
   * ------------------------------------------------------------------ */
  AsciiHero.prototype.drawBuildFrame = function (ef) {
    var v = this.view, ctx = this.ctx, pal = this.pal.colors;
    var maxI = CFG.paletteSize - 1;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = this.pal.voidStr;
    ctx.fillRect(0, 0, this.cssW, this.cssH);

    var win = CFG.buildCellWin;
    var churnBase = Math.round(0.24 * maxI); /* pre-arrival dim "code" */
    for (var y = 0; y < v.h; y++) {
      for (var x = 0; x < v.w; x++) {
        var i = y * v.w + x;
        if (!v.level[i] && v.spiral[i] === 0) continue;
        var a = (ef - v.buildDelay[i]) / win; /* 0..1 arrival */
        if (a <= 0) {
          /* not yet arrived: faint churning code */
          if (Math.random() < 0.5) continue; /* sparse -> reads as streaming */
          var g0 = 1 + Math.floor(Math.random() * DISP_MAX);
          ctx.font = this.fontFor(0.3);
          ctx.fillStyle = pal[churnBase + Math.floor(Math.random() * 4)];
          ctx.fillText(DISP[g0], this.offX + x * this.cell + this.cell / 2, this.offY + y * this.cell + this.cell / 2);
          continue;
        }
        var settle = a >= 1 ? 1 : a;
        /* target resting color, spiral gets its floor */
        var ci = Math.round(v.frac[i] * maxI);
        if (v.spiral[i] > 0) { var sci = Math.round(v.spiral[i] * CFG.spiralFloor * maxI); if (sci > ci) ci = sci; }
        ci = Math.round(ci * settle);
        /* brief overshoot flash toward skull as a cell locks in */
        if (a > 0.85 && a < 1.15) ci = Math.min(maxI, ci + 6);
        var g = a < 1 ? (1 + Math.floor(Math.random() * DISP_MAX)) : glyphForLevel(v.level[i] || 1);
        var cx = this.offX + x * this.cell + this.cell / 2;
        var cy = this.offY + y * this.cell + this.cell / 2;
        ctx.font = this.fontFor(v.frac[i] * settle);
        if (v.frac[i] >= CFG.extrudeMin && settle > 0.6) {
          ctx.fillStyle = pal[clampI(ci - CFG.extrudeDrop, 0, maxI)];
          ctx.fillText(DISP[g], cx + CFG.extrudePx, cy + CFG.extrudePx);
        }
        ctx.fillStyle = pal[clampI(ci, 0, maxI)];
        ctx.fillText(DISP[g], cx, cy);
      }
    }
  };

  /* ------------------------------------------------------------------ *
   * Reduced motion / QA: ONE static, fully-bright, resolved frame.
   * Drawn immediately and ungated. No layers, no loop, no motion.
   * ------------------------------------------------------------------ */
  AsciiHero.prototype.drawStatic = function () {
    var v = this.view;
    if (!v || !this.ctx) return;
    var ctx = this.ctx, pal = this.pal.colors, maxI = CFG.paletteSize - 1;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = this.pal.voidStr;
    ctx.fillRect(0, 0, this.cssW, this.cssH);
    for (var y = 0; y < v.h; y++) {
      for (var x = 0; x < v.w; x++) {
        var i = y * v.w + x;
        var lv = v.level[i];
        if (!lv && v.spiral[i] === 0) continue;
        var ci = v.baseCi[i];
        if (v.spiral[i] > 0) { var sci = Math.round(v.spiral[i] * CFG.spiralFloor * maxI); if (sci > ci) ci = sci; }
        var cx = this.offX + x * this.cell + this.cell / 2;
        var cy = this.offY + y * this.cell + this.cell / 2;
        ctx.font = this.fontFor(v.frac[i]);
        var ch = DISP[lv ? glyphForLevel(lv) : glyphForLevel(3)];
        if (v.frac[i] >= CFG.extrudeMin) {
          ctx.fillStyle = pal[clampI(ci - CFG.extrudeDrop, 0, maxI)];
          ctx.fillText(ch, cx + CFG.extrudePx, cy + CFG.extrudePx);
        }
        ctx.fillStyle = pal[clampI(ci, 0, maxI)];
        ctx.fillText(ch, cx, cy);
      }
    }
  };

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function boot(scope) {
    var nodes = (scope || document).querySelectorAll('canvas[data-msk-ascii]');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].__mskAscii) nodes[i].__mskAscii = new AsciiHero(nodes[i]);
    }
  }
  function unboot(scope) {
    var nodes = (scope || document).querySelectorAll('canvas[data-msk-ascii]');
    for (var i = 0; i < nodes.length; i++) if (nodes[i].__mskAscii) nodes[i].__mskAscii.destroy();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else { boot(); }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { unboot(e.target); });

  window.MSKAsciiHero = { boot: boot, version: '2.0.0' };
})();
